import React, { useEffect, useRef, useState } from 'react';
import Hls, { type PlaylistLoaderConstructor } from 'hls.js';
import LiveRecordGuide from './LiveRecordGuide';

const TZ = 'America/Martinique';
const ARCHIVE_SECONDS = 43200;
const STATUS_URL = '/live-dvr/status';
const LIVE_PLAYLIST = '/live-dvr/live.m3u8';

interface DvrRange {
  start: string;
  end: string;
}

interface DvrStatus {
  ok?: boolean;
  state?: string;
  message?: string;
  recording?: boolean;
  resolution?: string;
  range?: DvrRange | null;
}

function formatMartinique(ms: number, withSeconds = false): string {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
  }).format(date);
}

function toDatetimeLocal(ms: number): string {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

function zoneOffsetMs(date: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asUtc - date.getTime();
}

/** Interprète un datetime-local comme l'heure de la Martinique et renvoie un instant UTC en ms. */
function martiniqueLocalToMs(local: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!match) return NaN;
  const [, y, m, d, h, min] = match.map(Number);
  const guess = Date.UTC(y, m - 1, d, h, min, 0);
  let utc = guess - zoneOffsetMs(new Date(guess));
  const corrected = guess - zoneOffsetMs(new Date(utc));
  if (corrected !== utc) utc = corrected;
  return utc;
}

async function readMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    if (body && typeof body.message === 'string' && body.message.trim()) return body.message;
  } catch {
    /* corps non JSON */
  }
  return fallback;
}

/**
 * Les segments enregistrés avant le correctif Ubuntu recommencent chacun leur
 * horloge : il faut une coupure avant eux. Les segments postérieurs ont des
 * horodatages continus ; une coupure forcée y recalerait le son toutes les 6 s.
 * L'Ubuntu date aussi les anciens segments, d'où le repère horaire. Il devient
 * inutile quand l'archive a effacé ces segments (vers le 11/11/2026).
 */
const CONTINUOUS_SINCE_MS = Date.parse('2026-10-24T15:42:20.000Z');

function withDiscontinuities(playlist: string): string {
  const out: string[] = [];
  let pending: string[] = [];
  let segmentDate = NaN;
  let hasDiscontinuity = false;
  let previousContinuous: boolean | null = null;
  for (const line of playlist.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith('#EXTINF') || trimmed.startsWith('#EXT-X-PROGRAM-DATE-TIME') || trimmed.startsWith('#EXT-X-DISCONTINUITY')) {
      if (trimmed.startsWith('#EXT-X-PROGRAM-DATE-TIME:')) segmentDate = Date.parse(trimmed.slice('#EXT-X-PROGRAM-DATE-TIME:'.length));
      if (trimmed.startsWith('#EXT-X-DISCONTINUITY')) hasDiscontinuity = true;
      pending.push(line);
      continue;
    }
    if (trimmed.startsWith('#')) {
      out.push(line);
      continue;
    }
    const continuous = segmentDate >= CONTINUOUS_SINCE_MS;
    const needsCut = previousContinuous !== null && (!continuous || !previousContinuous);
    if (needsCut && !hasDiscontinuity) out.push('#EXT-X-DISCONTINUITY');
    out.push(...pending, line);
    pending = [];
    segmentDate = NaN;
    hasDiscontinuity = false;
    previousContinuous = continuous;
  }
  out.push(...pending);
  return out.join('\n');
}

/** Chargeur de playlists hls.js qui corrige chaque playlist reçue, y compris les rafraîchissements du direct. */
class DvrPlaylistLoader extends Hls.DefaultConfig.loader {
  constructor(config: ConstructorParameters<typeof Hls.DefaultConfig.loader>[0]) {
    super(config);
    const load = this.load.bind(this);
    this.load = (context, loaderConfig, callbacks) => {
      const onSuccess = callbacks.onSuccess;
      callbacks.onSuccess = (response, stats, ctx, networkDetails) => {
        if (typeof response.data === 'string') response.data = withDiscontinuities(response.data);
        onSuccess(response, stats, ctx, networkDetails);
      };
      load(context, loaderConfig, callbacks);
    };
  }
}

const LIVE_EDGE_MS = 45 * 1000;
const PROGRAM_LEAD_MS = 3 * 1000;

type PlayRequest =
  | { mode: 'live'; autoplay: boolean; nonce: number }
  | { mode: 'archive'; from: number; autoplay: boolean; nonce: number };

const RecordPlayer: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState<DvrStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [playError, setPlayError] = useState<string | null>(null);
  const [request, setRequest] = useState<PlayRequest | null>(null);
  const [positionMs, setPositionMs] = useState<number | null>(null);
  const [gotoValue, setGotoValue] = useState('');
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(STATUS_URL, { cache: 'no-store' });
        if (!res.ok) {
          const message = await readMessage(res, 'Enregistrement injoignable.');
          if (!cancelled) setStatusError(message);
          return;
        }
        const data = (await res.json()) as DvrStatus;
        if (cancelled) return;
        setStatus(data);
        setStatusError(null);
      } catch {
        if (!cancelled) setStatusError('Enregistrement injoignable.');
      }
    };
    load();
    const timer = window.setInterval(load, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const range = status?.range && status.range.start && status.range.end ? status.range : null;
  const rangeStart = range ? Date.parse(range.start) : NaN;
  const rangeEnd = range ? Date.parse(range.end) : NaN;
  const blocked = status?.state === 'waiting_disk' || status?.state === 'starting';

  useEffect(() => {
    if (request || !range || blocked) return;
    setRequest({ mode: 'live', autoplay: true, nonce: 0 });
  }, [request, range, blocked]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !request || blocked) return;
    let cancelled = false;
    let hls: Hls | null = null;

    const url = request.mode === 'live'
      ? LIVE_PLAYLIST
      : `/live-dvr/archive.m3u8?from=${encodeURIComponent(new Date(request.from).toISOString())}&duration=${ARCHIVE_SECONDS}`;

    const fail = (message: string) => {
      if (!cancelled) setPlayError(message);
    };

    // Ouverture de la page : lecture sans son, seule autorisée sans geste de l'utilisateur.
    if (request.nonce === 0) video.muted = true;
    const start = () => {
      if (cancelled || !request.autoplay) return;
      video.play().catch(() => {
        if (cancelled) return;
        video.muted = true;
        video.play().catch(() => {});
      });
    };

    const onTime = () => {
      const playing = hls?.playingDate ?? null;
      if (playing) {
        setPositionMs(playing.getTime());
        return;
      }
      const start = (video as HTMLVideoElement & { getStartDate?: () => Date }).getStartDate?.();
      if (start && !Number.isNaN(start.getTime()) && Number.isFinite(video.currentTime)) {
        setPositionMs(start.getTime() + video.currentTime * 1000);
      }
    };
    video.addEventListener('timeupdate', onTime);
    const onVolume = () => setMuted(video.muted || video.volume === 0);
    video.addEventListener('volumechange', onVolume);
    onVolume();

    (async () => {
      try {
        const res = await fetch(url, { cache: 'no-store' });
        const type = res.headers.get('content-type') || '';
        if (!res.ok || type.includes('json')) {
          fail(await readMessage(res, res.status === 404 ? 'Aucun enregistrement sur ce créneau.' : 'Enregistrement indisponible.'));
          return;
        }
        await res.body?.cancel();
        if (cancelled) return;
        setPlayError(null);
        setPositionMs(request.mode === 'archive' ? request.from : null);

        if (Hls.isSupported()) {
          hls = new Hls({
            lowLatencyMode: false,
            liveSyncDurationCount: 3,
            liveDurationInfinity: request.mode === 'live',
            maxBufferLength: 60,
            backBufferLength: 120,
            pLoader: DvrPlaylistLoader as unknown as PlaylistLoaderConstructor,
          });
          hls.on(Hls.Events.ERROR, (_event, data) => {
            if (!data.fatal || cancelled || !hls) return;
            if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
              hls.recoverMediaError();
              return;
            }
            fail('Lecture de l’enregistrement impossible.');
            hls.destroy();
            hls = null;
          });
          hls.on(Hls.Events.MANIFEST_PARSED, start);
          hls.loadSource(url);
          hls.attachMedia(video);
        } else {
          video.src = url;
          start();
        }
      } catch {
        fail('Enregistrement injoignable.');
      }
    })();

    return () => {
      cancelled = true;
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('volumechange', onVolume);
      if (hls) hls.destroy();
      video.removeAttribute('src');
      video.load();
    };
  }, [request, blocked]);

  const goLive = () => {
    setPlayError(null);
    setRequest((prev) => ({ mode: 'live', autoplay: true, nonce: (prev?.nonce || 0) + 1 }));
  };

  const loadAt = (target: number) => {
    if (!range) return;
    if (target >= rangeEnd - LIVE_EDGE_MS) {
      goLive();
      return;
    }
    const from = Math.max(rangeStart, target);
    setPlayError(null);
    setRequest((prev) => ({ mode: 'archive', from, autoplay: true, nonce: (prev?.nonce || 0) + 1 }));
  };

  /** Saute dans le flux déjà chargé si l'instant y figure, sinon recharge l'archive à partir de cet instant. */
  const seekTo = (target: number) => {
    if (!range) return;
    const video = videoRef.current;
    if (video && positionMs != null && target < rangeEnd - LIVE_EDGE_MS && Number.isFinite(video.currentTime)) {
      const time = video.currentTime + (target - positionMs) / 1000;
      for (let i = 0; i < video.seekable.length; i += 1) {
        if (time >= video.seekable.start(i) && time <= video.seekable.end(i) - 1) {
          setPlayError(null);
          video.currentTime = time;
          setPositionMs(target);
          video.play().catch(() => {});
          return;
        }
      }
    }
    loadAt(target);
  };

  const shift = (deltaMs: number) => {
    const current = positionMs ?? rangeEnd;
    seekTo(current + deltaMs);
  };

  /** Le playout et l'enregistreur n'ont pas tout à fait la même horloge : on démarre un peu avant. */
  const seekToProgram = (startMs: number) => seekTo(Math.max(rangeStart, startMs - PROGRAM_LEAD_MS));

  const onGoto = (event: React.FormEvent) => {
    event.preventDefault();
    if (!range || !gotoValue) return;
    const instant = martiniqueLocalToMs(gotoValue);
    if (Number.isNaN(instant) || instant < rangeStart || instant > rangeEnd) {
      setPlayError('Cet instant est hors de l’archive disponible.');
      return;
    }
    seekTo(instant);
  };

  const atLive = request?.mode === 'live' && (positionMs == null || rangeEnd - positionMs < LIVE_EDGE_MS);

  const resolution = status?.resolution ? status.resolution.replace(/x/i, '×') : '';

  return (
    <aside className="live-record-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#f8fafc' }}>Enregistrement</h2>
        {status?.state === 'recording' && (
          <span style={{
            background: '#ef4444', color: '#fff', borderRadius: 999, padding: '2px 8px',
            fontSize: 11, fontWeight: 800, letterSpacing: '0.04em',
          }}>REC</span>
        )}
        {resolution && <span style={{ color: '#94a3b8', fontSize: 13, marginLeft: 'auto' }}>{resolution}</span>}
      </div>

      {range && (
        <p style={{ margin: '0 0 10px', color: '#94a3b8', fontSize: 12, lineHeight: 1.4 }}>
          Disponible du {formatMartinique(rangeStart)} au {formatMartinique(rangeEnd)}
        </p>
      )}

      {blocked && status?.message && (
        <p style={{ margin: '0 0 10px', color: '#e2e8f0', fontSize: 14, lineHeight: 1.45 }}>{status.message}</p>
      )}

      {statusError && (
        <p style={{ margin: '0 0 8px', color: '#94a3b8', fontSize: 12 }}>{statusError}</p>
      )}

      {request && !blocked && (
        <>
          <div style={{
            position: 'relative', width: '100%', paddingTop: '56.25%', background: '#000',
            borderRadius: 8, overflow: 'hidden', marginBottom: 10,
          }}>
            <video
              ref={videoRef}
              controls
              playsInline
              preload="metadata"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            />
          </div>
          <p style={{ margin: '0 0 10px', color: '#f8fafc', fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            {atLive && (
              <span style={{
                background: '#f59e0b', color: '#121212', borderRadius: 999, padding: '2px 8px',
                fontSize: 11, fontWeight: 800, letterSpacing: '0.04em',
              }}>DIRECT</span>
            )}
            {positionMs != null ? formatMartinique(positionMs, true) : ''}
            {muted && (
              <button
                type="button"
                style={{ ...actionBtn, marginLeft: 'auto', padding: '4px 12px', fontSize: 13 }}
                onClick={() => {
                  const video = videoRef.current;
                  if (!video) return;
                  video.muted = false;
                  if (video.volume === 0) video.volume = 1;
                  video.play().catch(() => {});
                }}
              >
                Activer le son
              </button>
            )}
          </p>
        </>
      )}

      {playError && (
        <p style={{ margin: '0 0 8px', color: '#94a3b8', fontSize: 12 }}>{playError}</p>
      )}

      {range && !blocked && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <button type="button" style={actionBtn} onClick={() => shift(-60 * 60 * 1000)}>−1 h</button>
          <button type="button" style={actionBtn} onClick={() => shift(-30 * 60 * 1000)}>−30 min</button>
          <button type="button" style={actionBtn} onClick={() => shift(-10 * 60 * 1000)}>−10 min</button>
          <button type="button" style={actionBtn} onClick={() => shift(-60 * 1000)}>−1 min</button>
          <button type="button" style={actionBtn} onClick={() => shift(60 * 1000)}>+1 min</button>
          <button type="button" style={actionBtn} onClick={() => shift(10 * 60 * 1000)}>+10 min</button>
          <button type="button" style={actionBtn} onClick={() => shift(30 * 60 * 1000)}>+30 min</button>
          <button
            type="button"
            style={atLive ? actionBtn : { ...actionBtn, background: '#f59e0b', color: '#121212', borderColor: '#f59e0b' }}
            onClick={goLive}
          >
            Revenir au direct
          </button>
          <form onSubmit={onGoto} style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <label htmlFor="dvr-goto" style={{ color: '#94a3b8', fontSize: 13 }}>Aller à</label>
            <input
              id="dvr-goto"
              type="datetime-local"
              value={gotoValue}
              min={toDatetimeLocal(rangeStart)}
              max={toDatetimeLocal(rangeEnd)}
              onChange={(e) => setGotoValue(e.target.value)}
              style={{
                background: '#23262a', color: '#e2e8f0', border: '1px solid #353a40',
                borderRadius: 8, padding: '7px 8px', fontSize: 14,
              }}
            />
            <button type="submit" style={actionBtn}>OK</button>
          </form>
        </div>
      )}

      {range && !blocked && (
        <LiveRecordGuide
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          positionMs={positionMs}
          onSeek={seekTo}
          onPlayProgram={seekToProgram}
        />
      )}
    </aside>
  );
};

const actionBtn: React.CSSProperties = {
  background: '#23262a',
  color: '#cbd5e1',
  border: '1px solid #353a40',
  borderRadius: 8,
  padding: '8px 14px',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
};

class RecordBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return (
        <aside className="live-record-card">
          <p style={{ margin: 0, color: '#94a3b8', fontSize: 12 }}>Enregistrement indisponible.</p>
        </aside>
      );
    }
    return this.props.children;
  }
}

const LiveRecord: React.FC = () => (
  <RecordBoundary>
    <RecordPlayer />
  </RecordBoundary>
);

export default LiveRecord;
